import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getOptions, optionsUrlDependencies, resolveOptionsUrl } from "../../services/dynamicForms/options";
import useErrorModal from "../useErrorModal";

// types
import type { FieldDescriptor, FieldOption } from "../../types/components/dynamicForms";

type FormValues = Record<string, unknown>;

/** A select whose options load from a URL templated on other fields' values. */
interface CascadingField {
  name: string;
  optionsUrl: string;
}

/**
 * Cascading selects for a set of fields. A field with `optionsUrl` depends on the
 * fields its tokens name — on this page or, via `initialValues`, an earlier step.
 *
 * On mount (every remount — new step, new 226) each select whose dependencies
 * already have values loads its options, so a prefilled cascade never shows
 * missing or stale options. After that, when a dependency changes every dependent's
 * value is cleared (via the caller's `clearValue`) and its options reload against
 * the post-clear values, layered over `initialValues` so earlier-step tokens still
 * resolve.
 *
 * A load always empties the field's options first, so options for a previous parent
 * are never selectable while a request is in flight or after it fails. Only the
 * latest request per field may apply options or open the error modal, and nothing
 * is applied after unmount.
 *
 * Returns the fields with loaded options swapped in (for the schema and render)
 * and `reloadDependents` to call from the form's change subscription.
 * Form-library agnostic — the caller decides how a value is cleared.
 */
const useCascadingOptions = (fields: FieldDescriptor[], initialValues: FormValues) => {
  const openErrorModal = useErrorModal();

  // options loaded per field name; override the descriptor's initial options
  const [loadedOptions, setLoadedOptions] = useState<Record<string, FieldOption[]>>({});
  const latestOptionsPath = useRef<Record<string, string | null>>({});
  const isMounted = useRef(false);

  const resolvedFields = useMemo(
    () =>
      fields.map((field) => {
        const options = loadedOptions[field.name];
        return options ? { ...field, options } : field;
      }),
    [fields, loadedOptions]
  );

  const fieldsByName = useMemo(
    () =>
      resolvedFields.reduce<Record<string, FieldDescriptor>>((acc, field) => {
        acc[field.name] = field;
        return acc;
      }, {}),
    [resolvedFields]
  );

  const cascadingFields = useMemo(
    () =>
      fields.reduce<CascadingField[]>((acc, field) => {
        if (field.optionsUrl) acc.push({ name: field.name, optionsUrl: field.optionsUrl });
        return acc;
      }, []),
    [fields]
  );

  // field name → the cascading selects whose optionsUrl depends on it
  const dependentsOf = useMemo(
    () =>
      cascadingFields.reduce<Record<string, CascadingField[]>>((acc, cascading) => {
        optionsUrlDependencies(cascading.optionsUrl).forEach((dependency) => {
          acc[dependency] = (acc[dependency] ?? []).concat(cascading);
        });
        return acc;
      }, {}),
    [cascadingFields]
  );

  const loadOptions = useCallback(
    async (field: CascadingField, values: FormValues) => {
      const path = resolveOptionsUrl(field.optionsUrl, values);
      latestOptionsPath.current[field.name] = path;

      // still mounted, and not superseded by a newer request for this field
      const isLatest = () => isMounted.current && latestOptionsPath.current[field.name] === path;

      // drop the previous options first: an in-flight, failed or unresolvable load
      // must never leave options for an old parent selectable
      setLoadedOptions((prev) => ({ ...prev, [field.name]: [] }));

      // a dependency is empty: nothing to choose from until it's set
      if (path === null) return;

      try {
        const options = await getOptions(path);
        if (isLatest()) setLoadedOptions((prev) => ({ ...prev, [field.name]: options }));
      } catch (err) {
        if (isLatest()) openErrorModal(err);
      }
    },
    [openErrorModal]
  );

  // declared before the mount load so the guard is up when it runs
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Mount only — the renderer remounts (key) to re-seed. Selects whose dependencies
  // are still empty keep their descriptor options until a dependency is set.
  useEffect(() => {
    cascadingFields.forEach((field) => {
      const dependenciesResolved = resolveOptionsUrl(field.optionsUrl, initialValues) !== null;
      if (dependenciesResolved) loadOptions(field, initialValues);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Clear and reload every select that depends on `changedName`. All dependents are
   * cleared first — in the form and in a local copy of the values — so each reload
   * resolves its URL against the post-clear values, never a sibling being cleared.
   * The copy layers this page's values over `initialValues`, so a token naming an
   * earlier-step field resolves the same way it did on mount.
   */
  const reloadDependents = useCallback(
    (changedName: string, values: FormValues, clearValue: (name: string) => void) => {
      const dependents = dependentsOf[changedName] ?? [];
      const clearedValues: FormValues = { ...initialValues, ...values };

      dependents.forEach((dependent) => {
        clearedValues[dependent.name] = "";
        clearValue(dependent.name);
      });

      dependents.forEach((dependent) => {
        loadOptions(dependent, clearedValues);
      });
    },
    [dependentsOf, loadOptions, initialValues]
  );

  return { resolvedFields, fieldsByName, reloadDependents };
};

export default useCascadingOptions;